'use strict'

import {
  EventBus,
  IMAGE_MODAL_OPEN_EVENT,
  VIDEO_PLAY_EVENT,
  VIDEO_RESUME_EVENT,
  VIDEO_PAUSE_EVENT,
  VIDEO_ENDED_EVENT,
  TOOL_USE_EVENT,
  NAVIGATE_EVENT,
  VIEW_EVENT,
  QUESTION_PAGE_NAVIGATE_EVENT,
  QUESTION_PAGE_NAVIGATE_PARTIAL_EVENT,
  AUDIO_PLAY_EVENT,
  AUDIO_RESUME_EVENT,
  AUDIO_PAUSE_EVENT,
  AUDIO_ENDED_EVENT,
  ENABLE_REVIEW_EVENT
} from '../common/EventBus'

import {
  ThasClient,
  ThasViewEvent,
  ThasNavigationEvent,
  ThasMediaEvent,
  ThasToolUseEvent,
} from '@thiememeulenhoff/analytics-service-client';

export const NAVIGATION_EVENT_ACTION_NAVIGATE_TO = 'navigateTo';
export const MEDIA_EVENT_MEDIA_TYPE_JOJOSCHOOL = 'jojoschool';
export const MEDIA_EVENT_MEDIA_TYPE_VIDEO = 'video';
export const MEDIA_EVENT_MEDIA_TYPE_AUDIO = 'audio';
export const MEDIA_EVENT_MEDIA_TYPE_IMAGE = 'image';
export const MEDIA_EVENT_ACTION_STARTED = 'started';
export const MEDIA_EVENT_ACTION_ENDED = 'ended';
export const MEDIA_EVENT_ACTION_PAUSED = 'paused';
export const MEDIA_EVENT_ACTION_RESUMED = 'resumed';
export const MEDIA_EVENT_ACTION_ENLARGED = 'enlarged';
export const VIEW_EVENT_TAB_ACTIVE = 'active';
export const VIEW_EVENT_TAB_INACTIVE = 'inactive';
export const VIEW_EVENT_INTERVAL_MS = 60000

class ThasService {
  constructor (client, actor, arrangementCode, role, sessionId) {
    this._client = client;
    this._actor = actor;
    this._role = role;
    this._arrangementCode = arrangementCode;
    this._sessionId = sessionId;

    this._client.setArrangementCode(this._arrangementCode);

    this._sendNavigationEvent(
      this._cleanLocation(document.location.href),
      this._cleanLocation(document.location.referer)
    )

    this._pollCount = 0;
    setInterval(() => {
      this._sendViewEvent(
        this._cleanLocation(document.location.href),
        this._pollCount
      )
      this._pollCount++
    }, VIEW_EVENT_INTERVAL_MS)

    EventBus.$on(IMAGE_MODAL_OPEN_EVENT, (imageId) => {
      this._sendMediaEvent(imageId, MEDIA_EVENT_ACTION_ENLARGED, MEDIA_EVENT_MEDIA_TYPE_IMAGE)
    })

    EventBus.$on(VIDEO_PLAY_EVENT, (vmsId, customEventType) => {
      this._sendMediaEvent(vmsId, MEDIA_EVENT_ACTION_STARTED, customEventType ?? MEDIA_EVENT_MEDIA_TYPE_VIDEO)
    })

    EventBus.$on(VIDEO_RESUME_EVENT, (vmsId, customEventType) => {
      this._sendMediaEvent(vmsId, MEDIA_EVENT_ACTION_RESUMED, customEventType ?? MEDIA_EVENT_MEDIA_TYPE_VIDEO)
    })

    EventBus.$on(VIDEO_PAUSE_EVENT, (vmsId, customEventType) => {
      this._sendMediaEvent(vmsId, MEDIA_EVENT_ACTION_PAUSED, customEventType ?? MEDIA_EVENT_MEDIA_TYPE_VIDEO)
    })

    EventBus.$on(VIDEO_ENDED_EVENT, (vmsId, customEventType) => {
      this._sendMediaEvent(vmsId, MEDIA_EVENT_ACTION_ENDED, customEventType ?? MEDIA_EVENT_MEDIA_TYPE_VIDEO)
    })

    EventBus.$on(AUDIO_PLAY_EVENT, (vmsId) => {
      this._sendMediaEvent(vmsId, MEDIA_EVENT_ACTION_STARTED, MEDIA_EVENT_MEDIA_TYPE_AUDIO)
    })

    EventBus.$on(AUDIO_RESUME_EVENT, (vmsId) => {
      this._sendMediaEvent(vmsId, MEDIA_EVENT_ACTION_RESUMED, MEDIA_EVENT_MEDIA_TYPE_AUDIO)
    })

    EventBus.$on(AUDIO_PAUSE_EVENT, (vmsId) => {
      this._sendMediaEvent(vmsId, MEDIA_EVENT_ACTION_PAUSED, MEDIA_EVENT_MEDIA_TYPE_AUDIO)
    })

    EventBus.$on(AUDIO_ENDED_EVENT, (vmsId) => {
      this._sendMediaEvent(vmsId, MEDIA_EVENT_ACTION_ENDED, MEDIA_EVENT_MEDIA_TYPE_AUDIO)
    })

    EventBus.$on(TOOL_USE_EVENT, (href, toolType) => {
      this._sendToolUseEvent(href, toolType)
    })

    EventBus.$on(NAVIGATE_EVENT, (href) => {
      this._sendNavigationEvent(href, this._cleanLocation(document.location.href))
    })

    EventBus.$on(QUESTION_PAGE_NAVIGATE_EVENT, (href) => {
      this._sendNavigationEvent(href, this._cleanLocation(document.location.href))
    })

    EventBus.$on(QUESTION_PAGE_NAVIGATE_PARTIAL_EVENT, (href) => {
      this._sendNavigationEvent(href, this._cleanLocation(document.location.href))
    })

    EventBus.$on(VIEW_EVENT, (href, iteration) => {
      this._sendViewEvent(href, iteration)
    })

  }

  _cleanLocation(url) {
    if (undefined === url || null === url) {
      return null
    }

    const parts = url.split('#')
    if (parts.length > 1 && parts[1].length > 50) {
      return parts[0]
    }

    return url;
  }

  _absoluteUri(href) {
    try {
      return new URL(href, document.baseURI).href
    } catch(err) {}
    return href
  }

  _sendNavigationEvent(href, referer) {
    this._sendEvent(new ThasNavigationEvent(
      NAVIGATION_EVENT_ACTION_NAVIGATE_TO,
      this._absoluteUri(href),
      referer,
    ))
  }

  _sendMediaEvent (assetId, action, type) {
    this._sendEvent(new ThasMediaEvent(
      type,
      action,
      assetId,
    ))
  }

  _sendToolUseEvent(href, toolType) {
    const referer = this._cleanLocation(document.location.href)
    const target = toolType || href

    this._sendEvent(new ThasToolUseEvent(
      target,
      referer,
    ))
  }

  _sendViewEvent(href, iteration) {
    this._sendEvent(new ThasViewEvent(
      this._absoluteUri(href),
      iteration,
      document.hasFocus() ? VIEW_EVENT_TAB_ACTIVE : VIEW_EVENT_TAB_INACTIVE,
    ))
  }

  _sendEvent (event) {
    try {
      this._client.send(event);
    } catch (e) {
      console.error('Thas error', e);
    }
  }
}

export class ThasServiceFactory {
  static createThasService(configuration) {
    const thasClient = new ThasClient({
      token: configuration.token,
      baseUrl: configuration.baseUrl,
    });

    thasClient.setActor({
      actor: configuration.actor,
      role: configuration.role,
    });

    return new ThasService(thasClient, configuration.actor, configuration.arrangementCode, configuration.role, configuration.sessionId)
  }
}
